import React, { useEffect, useState } from "react";
import { Grid } from "@material-ui/core";
import { Typography } from "@welldigital/components";

import AddComment from "./CommentSection/AddComment";
import CommentSection from "./CommentSection/CommentSection";
import ViewImagesDialog from "./ViewImageDialog/ViewImagesDialog";
import ViewConsultation from "./ViewConsultation/ViewConsultation";
import WarningBox from "../WarningBox";

import OrderItemsForReviewWrapper, {
  PropsTypes,
} from "./OrderItemsForReviewWrapper";

import "./index.scss";
import { formatDate } from "utils/time";
import { formatPrice } from "utils/formatPrice";
import { useAppSelector } from "redux/hooks";
import { selectUserGroup } from "pages/ClinicalReviewPage/reduxSlice/selector";
import { CLINICAL_REVIEW_USER_GROUP } from "../../../../constants";
import { Products } from "pages/ClinicalReviewPage/Types";
import UploadImage from "../UploadImageContainer/UploadImage";
import ConfirmBMI from "../ConfirmBMI/ConfirmBMI";

const IMAGE_UPLOAD_SECONDARY_TAGS = [
  "onlinedoctoracne",
  "onlinedoctoreczema",
  "onlinedoctorhairloss",
  "onlinedoctorweightloss",
];

export const OrderItemsForReview = ({
  product,
  orderId,
  customerId,
  orderDate,
  subscriptionBaseOrderId,
  showHistory,
  idCheckStatus,
  idCheckApiStatus,
  isApprovedAllGSL,
  restrictedItemsApproval,
  onUpdateImages,
  orderNotesForConfirmBMI
}: PropsTypes) => {
  const {
    reviewComment,
    reviewDate,
    reviewedBy,
    orderItemId,
    warningCount = 0,
    exclusionCount,
    lastTimeOrderedOn,
  } = product;
  const [isConfirmBMI, setIsConfirmBMI] = useState(true);
  const userGroup = useAppSelector(selectUserGroup);
  const comment = reviewComment && reviewDate && reviewedBy ? [{ reviewComment, reviewDate, reviewedBy }] : [];
  const secondaryTag = product.secondaryTag?.trim().toLowerCase() ?? "";
  const supportsImageUpload = IMAGE_UPLOAD_SECONDARY_TAGS.includes(secondaryTag);
  const isApprovebuttonEnable = (!product.isPhotoUploadSkipped || (orderNotesForConfirmBMI.length > 0));
  const pgdScopeWarnings = [
    {
      id: "pgd-scope-warning",
      descriptions: [
        "Approval for this medication can only be done by a trained Independent Prescriber. By approving this item, you confirm that you are acting as an Independent Prescriber and have reviewed the full detail of this order, patient and their history. You must include a review note before you approve or reject this item",
      ],
    },
  ];

  orderNotesForConfirmBMI.forEach(notes => {
    comment.push({ reviewComment: notes.noteText, reviewDate: notes.createAt, reviewedBy: notes.createdBy })
  });
  useEffect(() => {
    if (orderNotesForConfirmBMI.length > 0) {
      setIsConfirmBMI(false);
    } else {
      setIsConfirmBMI(product.isPhotoUploadSkipped || false);
    }
  }, [product.isPhotoUploadSkipped, orderNotesForConfirmBMI]);

  return (
    <>
      <div>
        <Grid container className="crp_order_item_container">
          <Grid item xs={12} sm={6} md={4} className="flex-column">
            <Typography variant="subtitle2" spacingAfter={1}>
              Name
            </Typography>
            <Typography variant="caption">{product.productTitle}</Typography>
            {product.productVariation && (
              <Typography variant="caption">
                {product.productVariation}
              </Typography>
            )}
            {showHistory && !!product.subscriptionId && (
              <>
                <Typography variant="caption">
                  Subs Id: {product.subscriptionId}
                </Typography>
                <Typography variant="caption">
                  Issue number: {product.subscriptionIssueNumber}
                </Typography>
              </>
            )}
            {lastTimeOrderedOn && (
              <Typography className="color-warning" variant="h6">
                Last ordered on:{" "}
                {formatDate(lastTimeOrderedOn, "dd/MM/yy hh:mm aa")}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subtitle2" spacingAfter={1}>
              Price
            </Typography>
            <Typography variant="caption">
              {formatPrice(+product.productPrice)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subtitle2" spacingAfter={1}>
              Qty
            </Typography>
            <Typography variant="caption" spacingAfter={1}>
              {product.productQuantity}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography
              variant="subtitle2"
              className="warning_box"
              spacingAfter={1}
            >
              Warnings
            </Typography>
            <Typography
              variant="caption"
              className="warning_box"
              spacingAfter={1}
            >
              {warningCount !== 0 && isConfirmBMI ? warningCount + 1 : warningCount === 0 ? "None" : warningCount}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2} justifyContent="flex-end">
            <Typography
              variant="subtitle2"
              className="warning_box"
              spacingAfter={1}
            >
              Exclusions
            </Typography>
            <Typography
              variant="caption"
              className="warning_box"
              spacingAfter={1}
            >
              {product.exclusionCount || "None"}
            </Typography>
          </Grid>
        </Grid>
        <Grid container className="crp_comments">
          {(product?.reviewStatus.toLowerCase() === "approved" ||
            product?.reviewStatus.toLowerCase() === "rejected" || comment.length > 0) && (
              <CommentSection
                comment={comment}
              />
            )}
          <Grid item className="crp_order_item_button">
            {supportsImageUpload && (<UploadImage
                productId={product.productId}
                orderId={subscriptionBaseOrderId ?? orderId}
                onUpdateImages={onUpdateImages}
                categoryType={secondaryTag}
              />)}
            {isConfirmBMI && secondaryTag === "onlinedoctorweightloss" && <ConfirmBMI productId={product.productId}
              orderId={subscriptionBaseOrderId ?? orderId} onUpdateImages={onUpdateImages} />}
            {!showHistory &&
              !product.isPhotoUploadSkipped && supportsImageUpload && (
                  <ViewImagesDialog
                  secondaryTag={secondaryTag}
                    orderId={subscriptionBaseOrderId ?? orderId}
                  />
              )}
            <ViewConsultation
              orderDate={orderDate}
              traversalId={product.traversalId}
              secondaryTag={secondaryTag}
            />
          </Grid>

          {!showHistory &&
            hasAccessToApproveOrDecline(product, userGroup) &&
            (product?.reviewStatus.toLowerCase() === "awaiting review" ||
              product?.reviewStatus.toLowerCase() ===
              "awaiting clinical review") && (
              <Grid item className="crp_order_item_commentbox">
                {product.crpSubscriptionBanner && (
                  <WarningBox
                    className="crp_order_details_pgd_warning"
                    title="This medication is outside of PGD scope."
                    warnings={pgdScopeWarnings}
                  />
                )}
                <AddComment
                  orderId={orderId}
                  productId={orderItemId}
                  productName={product.productTitle}
                  customerId={customerId}
                  wCount={warningCount || exclusionCount}
                  idCheckStatus={idCheckStatus}
                  idCheckApiStatus={idCheckApiStatus}
                  isApprovedAllGSL={isApprovedAllGSL}
                  restrictedItemsApproval={restrictedItemsApproval}
                  isApprovebuttonEnable={isApprovebuttonEnable}
                  crpSubscriptionCheckBox={product.crpSubscriptionCheckBox}
                />
              </Grid>
            )}
        </Grid>
      </div>
    </>
  );
};

export default OrderItemsForReviewWrapper(OrderItemsForReview);

function hasAccessToApproveOrDecline(
  product: Products,
  userGroup?: CLINICAL_REVIEW_USER_GROUP,
) {
  /**
   * Permission: Can approve/decline POMs
   * Pharmacist: No
   * Independent Prescriber: Yes
   * Customer Service Agent: No
   */
  if (
    product.primaryTag?.trim().toLowerCase() === "pom" &&
    [CLINICAL_REVIEW_USER_GROUP.CR_INDEPENDENT_PRESCRIBER].includes(userGroup!)
  ) {
    return true;
  }

  /**
   * Permission: Can approve/decline PMEDs
   * Pharmacist: Yes
   * Independent Prescriber: Yes
   * Customer Service Agent: No
   */
  if (
    product.primaryTag?.trim().toLowerCase() === "pmed" &&
    [
      CLINICAL_REVIEW_USER_GROUP.CR_INDEPENDENT_PRESCRIBER,
      CLINICAL_REVIEW_USER_GROUP.CR_PHARMACIST,
    ].includes(userGroup!)
  ) {
    return true;
  }

  return false;
}
